import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, ImagePlus, ShieldAlert, Upload, X } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react'
import { useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Input } from '../../ui/Input/Input'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenant/TenantProvider'
import { loadDeploymentSha } from './deployment-metadata'
import {
  issueCategories,
  issueAttachmentMaxBytes,
  issueAttachmentTypes,
  issueReportPayloadSchema,
  mapDashboardRoute,
  type IssueCategory,
  type IssueReportAttachment,
  type IssueReportPayload,
  type IssueRoute,
} from './issue-report-contracts'
import {
  createIssueReportRepository,
  isIssueReportRateLimitError,
  type IssueReportRepository,
} from './issue-report-repository'
import styles from './ReportIssuePage.module.css'

const categoryLabels: Record<IssueCategory, string> = {
  general: 'General',
  navigation: 'Navigation',
  'data-display': 'Data display',
  performance: 'Performance',
  other: 'Other',
}

type Draft = {
  category: IssueCategory
  subject: string
  description: string
}

const emptyDraft: Draft = {
  category: 'general',
  subject: '',
  description: '',
}

const attachmentAccept = issueAttachmentTypes.join(',')

type SelectedImage = {
  fileName: string
  previewUrl: string
  payload: IssueReportAttachment
}

function readImage(file: File) {
  return new Promise<SelectedImage>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('read_failed'))
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('read_failed'))
        return
      }
      const separator = reader.result.indexOf(',')
      if (separator < 0) {
        reject(new Error('read_failed'))
        return
      }
      resolve({
        fileName: file.name,
        previewUrl: reader.result,
        payload: {
          contentType: file.type as IssueReportAttachment['contentType'],
          sizeBytes: file.size,
          base64Data: reader.result.slice(separator + 1),
        },
      })
    }
    reader.readAsDataURL(file)
  })
}

function newRequestId() {
  return crypto.randomUUID()
}

export function ReportIssueContent({
  tenantId,
  originRoute,
  repository,
  createClientRequestId = newRequestId,
  deploymentShaLoader = loadDeploymentSha,
}: {
  tenantId: string
  originRoute: IssueRoute
  repository: IssueReportRepository
  createClientRequestId?: () => string
  deploymentShaLoader?: () => Promise<string | null>
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [clientRequestId, setClientRequestId] = useState(createClientRequestId)
  const [availableDeploymentSha, setAvailableDeploymentSha] = useState<
    string | null
  >(null)
  const [frozenDeploymentSha, setFrozenDeploymentSha] = useState<
    string | null | undefined
  >(undefined)
  const [showValidation, setShowValidation] = useState(false)
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [readingAttachment, setReadingAttachment] = useState(false)
  const attachmentInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let active = true
    void deploymentShaLoader().then((sourceSha) => {
      if (active) setAvailableDeploymentSha(sourceSha)
    })
    return () => {
      active = false
    }
  }, [deploymentShaLoader])

  const candidatePayload = useMemo<IssueReportPayload>(
    () => ({
      ...draft,
      route: originRoute,
      clientRequestId,
      deploymentSha:
        frozenDeploymentSha === undefined
          ? availableDeploymentSha
          : frozenDeploymentSha,
      ...(selectedImage ? { attachment: selectedImage.payload } : {}),
    }),
    [
      availableDeploymentSha,
      clientRequestId,
      draft,
      frozenDeploymentSha,
      originRoute,
      selectedImage,
    ]
  )
  const validation = useMemo(
    () => issueReportPayloadSchema.safeParse(candidatePayload),
    [candidatePayload]
  )
  const fieldErrors = useMemo(() => {
    const errors: Partial<Record<keyof Draft, string>> = {}
    if (!showValidation || validation.success) return errors
    for (const issue of validation.error.issues) {
      const field = issue.path[0]
      if (
        field === 'category' ||
        field === 'subject' ||
        field === 'description'
      ) {
        errors[field] ??= issue.message
      }
    }
    return errors
  }, [showValidation, validation])

  const submission = useMutation({
    mutationFn: (payload: IssueReportPayload) =>
      repository.submit(tenantId, payload),
  })

  const formLocked = submission.isPending || submission.isError

  async function selectImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setAttachmentError(null)

    if (!issueAttachmentTypes.includes(file.type as never)) {
      setAttachmentError('Choose a PNG, JPEG, or WebP image.')
      event.target.value = ''
      return
    }
    if (file.size === 0 || file.size > issueAttachmentMaxBytes) {
      setAttachmentError('Image must be smaller than 2 MB.')
      event.target.value = ''
      return
    }

    setReadingAttachment(true)
    try {
      setSelectedImage(await readImage(file))
    } catch {
      setAttachmentError('This image could not be read. Choose another image.')
      event.target.value = ''
    } finally {
      setReadingAttachment(false)
    }
  }

  function removeImage() {
    setSelectedImage(null)
    setAttachmentError(null)
    if (attachmentInput.current) attachmentInput.current.value = ''
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submission.isPending || submission.isSuccess) return
    setShowValidation(true)
    const deploymentSha =
      frozenDeploymentSha === undefined
        ? availableDeploymentSha
        : frozenDeploymentSha
    const parsed = issueReportPayloadSchema.safeParse({
      ...draft,
      route: originRoute,
      clientRequestId,
      deploymentSha,
      ...(selectedImage ? { attachment: selectedImage.payload } : {}),
    })
    if (!parsed.success) return
    if (frozenDeploymentSha === undefined) setFrozenDeploymentSha(deploymentSha)
    submission.mutate(parsed.data)
  }

  function resetForm() {
    setDraft(emptyDraft)
    setClientRequestId(createClientRequestId())
    setFrozenDeploymentSha(undefined)
    setShowValidation(false)
    removeImage()
    submission.reset()
  }

  if (submission.isSuccess) {
    return (
      <Card className={styles.success} role="status">
        <CheckCircle2 size={32} aria-hidden="true" />
        <h2>Issue submitted</h2>
        <p>
          Your issue reference is <strong>{submission.data.issueId}</strong>.
        </p>
        <Button type="button" onClick={resetForm}>
          Report another issue
        </Button>
      </Card>
    )
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Dashboard support</span>
        <h2>Report an issue</h2>
        <p>
          Share what happened and, if useful, add a screenshot for the Flio
          support team.
        </p>
      </header>

      <Card className={styles.card}>
        <form className={styles.form} noValidate onSubmit={submit}>
          <label className={styles.field} htmlFor="issue-category">
            <span>Category</span>
            <select
              id="issue-category"
              value={draft.category}
              disabled={formLocked}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  category: event.target.value as IssueCategory,
                }))
              }
            >
              {issueCategories.map((category) => (
                <option key={category} value={category}>
                  {categoryLabels[category]}
                </option>
              ))}
            </select>
          </label>

          <div>
            <Input
              id="issue-subject"
              label="Subject"
              maxLength={80}
              value={draft.subject}
              disabled={formLocked}
              aria-invalid={Boolean(fieldErrors.subject)}
              aria-describedby={
                fieldErrors.subject ? 'issue-subject-error' : undefined
              }
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  subject: event.target.value,
                }))
              }
            />
            {fieldErrors.subject ? (
              <p id="issue-subject-error" className={styles.error} role="alert">
                {fieldErrors.subject}
              </p>
            ) : null}
          </div>

          <div className={styles.privacyGuidance} role="note">
            <ShieldAlert size={20} aria-hidden="true" />
            <div>
              <strong>Keep patient information out</strong>
              <p>
                Do not include patient names, phone numbers, dates of birth,
                appointment details, conversation text, recordings, or other
                patient information in the description or screenshot.
              </p>
            </div>
          </div>

          <label className={styles.field} htmlFor="issue-description">
            <span>Description</span>
            <textarea
              id="issue-description"
              aria-label="Description"
              rows={8}
              maxLength={1000}
              value={draft.description}
              disabled={formLocked}
              aria-invalid={Boolean(fieldErrors.description)}
              aria-describedby={
                fieldErrors.description ? 'issue-description-error' : undefined
              }
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
            <span className={styles.counter}>
              {draft.description.length}/1000
            </span>
          </label>
          {fieldErrors.description ? (
            <p
              id="issue-description-error"
              className={styles.error}
              role="alert"
            >
              {fieldErrors.description}
            </p>
          ) : null}

          <div className={styles.attachmentField}>
            <div className={styles.attachmentHeading}>
              <span>Screenshot</span>
              <small>Optional · PNG, JPEG, or WebP · 2 MB max</small>
            </div>
            <input
              ref={attachmentInput}
              className={styles.fileInput}
              id="issue-screenshot"
              type="file"
              aria-label="Screenshot"
              accept={attachmentAccept}
              disabled={formLocked || readingAttachment}
              aria-describedby={
                attachmentError
                  ? 'issue-screenshot-help issue-screenshot-error'
                  : 'issue-screenshot-help'
              }
              onChange={(event) => void selectImage(event)}
            />
            <span id="issue-screenshot-help" className={styles.srOnly}>
              Add one optional screenshot without patient information.
            </span>
            {selectedImage ? (
              <div className={styles.attachmentPreview}>
                <img
                  src={selectedImage.previewUrl}
                  alt="Selected screenshot preview"
                />
                <div className={styles.attachmentDetails}>
                  <strong>{selectedImage.fileName}</strong>
                  <span>
                    {(selectedImage.payload.sizeBytes / 1024).toFixed(0)} KB
                  </span>
                  <div className={styles.attachmentActions}>
                    <Button
                      variant="secondary"
                      type="button"
                      disabled={formLocked}
                      onClick={() => attachmentInput.current?.click()}
                    >
                      <Upload size={16} aria-hidden="true" /> Change image
                    </Button>
                    <Button
                      variant="ghost"
                      type="button"
                      disabled={formLocked}
                      onClick={removeImage}
                    >
                      <X size={16} aria-hidden="true" /> Remove
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <label
                className={styles.attachmentDropzone}
                htmlFor="issue-screenshot"
              >
                <span className={styles.uploadIcon}>
                  <ImagePlus size={21} aria-hidden="true" />
                </span>
                <span>
                  <strong>
                    {readingAttachment ? 'Preparing image…' : 'Add screenshot'}
                  </strong>
                  <small>Choose an image from this device</small>
                </span>
              </label>
            )}
            {attachmentError ? (
              <p
                id="issue-screenshot-error"
                className={styles.error}
                role="alert"
              >
                {attachmentError}
              </p>
            ) : null}
          </div>

          {submission.isError ? (
            <div className={styles.failure} role="alert">
              <strong>
                {isIssueReportRateLimitError(submission.error)
                  ? 'Too many reports'
                  : 'Issue could not be submitted'}
              </strong>
              <p>
                {isIssueReportRateLimitError(submission.error)
                  ? 'Please wait before submitting another issue.'
                  : 'Your information is preserved. Retry when the Dashboard service is available.'}
              </p>
            </div>
          ) : null}

          <div className={styles.actions}>
            <Button
              type="submit"
              disabled={submission.isPending || readingAttachment}
            >
              {submission.isPending
                ? 'Submitting…'
                : submission.isError
                  ? 'Retry'
                  : 'Submit issue'}
            </Button>
            {submission.isError ? (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Start over
              </Button>
            ) : null}
          </div>
        </form>
      </Card>
    </section>
  )
}

export function ReportIssuePage({
  repository,
}: {
  repository?: IssueReportRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const location = useLocation()
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const activeRepository = useMemo(
    () =>
      repository ?? createIssueReportRepository({ getUser: () => currentUser }),
    [currentUser, repository]
  )
  const state = location.state as { originPathname?: unknown } | null
  const originRoute = mapDashboardRoute(state?.originPathname)

  if (!tenant.selectedClinic) {
    return <Spinner label="Loading clinic" />
  }

  return (
    <ReportIssueContent
      key={tenant.selectedClinic.id}
      tenantId={tenant.selectedClinic.id}
      originRoute={originRoute}
      repository={activeRepository}
    />
  )
}
